import React, { useState, useEffect } from 'react';
import { useStatus } from '../context/StatusContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { FaSignOutAlt } from 'react-icons/fa';
import '../styles/mobile/MobileHeader.css';
import { getApiBase } from '../utils/apiBase';

const MobileHeader = () => {
  const [timestamps, setTimestamps] = useState({});
  const { pageStatuses } = useStatus();
  const [isHidden, setIsHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY) {
        setIsHidden(false);
      } else if (currentScrollY > lastScrollY && currentScrollY > 45) {
        setIsHidden(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    const fetchTimestamps = async () => {
      try {
        const API = getApiBase();
        const response = await fetch(`${API}/export_timestamps.json`);
        const data = await response.json();

        const createTimestamp = (timestampStr) => {
          if (!timestampStr) return new Date(Date.now());
          const date = new Date(timestampStr);
          return isNaN(date.getTime()) ? new Date(Date.now()) : date;
        };
        
        const pageTimestamps = {
          'FTSO': {
            timestamp: createTimestamp(
              Math.min(
                data['submission_summary.json']?.last_updated ? new Date(data['submission_summary.json'].last_updated).getTime() : Date.now(),
                data['pivot_voting_round.json']?.last_updated ? new Date(data['pivot_voting_round.json'].last_updated).getTime() : Date.now()
              )
            ),
            status: pageStatuses.ftsoSubmissions
          },
          'FDC': {
            timestamp: createTimestamp(
              Math.min(
                data['fdc_submission_summary.json']?.last_updated ? new Date(data['fdc_submission_summary.json'].last_updated).getTime() : Date.now(),
                data['fdc_submissions.json']?.last_updated ? new Date(data['fdc_submissions.json'].last_updated).getTime() : Date.now()
              )
            ),
            status: pageStatuses.fdcSubmissions
          },
          'NODES': {
            timestamp: createTimestamp(data['node_health.json']?.last_updated),
            status: pageStatuses.nodes
          },
          'VAL': {
            timestamp: createTimestamp(data['validators_status.json']?.last_updated),
            status: pageStatuses.validators
          },
          'INDEXERS': {
            timestamp: createTimestamp(data['indexer_states.json']?.last_updated),
            status: pageStatuses.indexers
          },
          'FU': {
            timestamp: createTimestamp(data['fast_updates.json']?.last_updated),
            status: pageStatuses.fastUpdates
          },
          'REG': {
            timestamp: createTimestamp(data['registration_status.json']?.last_updated),
            status: pageStatuses.registration
          },
          'BAL': {
            timestamp: createTimestamp(data['balances_status.json']?.last_updated),
            status: pageStatuses.balances
          }
        };

        // Only update if changed to avoid paint flicker
        setTimestamps(prev => {
          try {
            return JSON.stringify(prev) === JSON.stringify(pageTimestamps) ? prev : pageTimestamps;
          } catch {
            return pageTimestamps;
          }
        });
      } catch (error) {
        console.error('Error fetching or processing timestamps in MobileHeader:', error);
        // Consider setting a default/error state for timestamps if the entire fetch fails
      }
    };

    fetchTimestamps();
    const interval = setInterval(fetchTimestamps, 20000);
    // On focus/visibility, refresh immediately for snappy header
    const refresh = () => fetchTimestamps().catch(() => {});
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refresh();
    });
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [pageStatuses]);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'UTC'
    });
  };


  return (
    <header className={`mobile-header ${isHidden ? 'hidden' : ''}`}>
      <div className="user-info-bar">
        <div className="user-container">
          <img 
            src="https://avatars.githubusercontent.com/u/6796665" 
            alt="User Avatar" 
            className="mobile-avatar"
          />
          <span className="mobile-username">{user?.email || 'Guest'}</span>
          <button onClick={handleSignOut} className="mobile-sign-out">
            <FaSignOutAlt />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
      <div className="status-grid">
        {Object.entries(timestamps).map(([pageLabel, data]) => (
          <div key={pageLabel} className={`status-item bg-${((data && data.status) ? String(data.status).toLowerCase().replace('yellow','amber') : 'grey')}`}>
            <span className="status-label">{pageLabel}</span>
            <span className="status-time">
              ({formatTimestamp(data.timestamp)})
            </span>
          </div>
        ))}
      </div>
    </header>
  );
};


export default MobileHeader;